import { Menu } from "@base-ui/react/menu"
import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { preventDefaultAndStopPropagation } from "shared/lib/preventDefaultAndStopPropagation"
import { Action } from "shared/types/Action"
import { IconSize } from "shared/types/Icon"
import { WithClassName } from "shared/types/WithClassName"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import "shared/ui/menu/styles/_menu.scss"

import { useProjectActions } from "features/project-management/model/useProjectActions"
import { FilteredProjectScopedActionHandlers } from "features/project-management/model/useProjectScopedActionHandler"
import { ProjectActionType } from "features/project-management/types/ProjectActionType"

export interface ProjectActionsButtonProps
  extends Partial<FilteredProjectScopedActionHandlers>,
    WithClassName {
  isDisabled?: boolean

  isDefaultOpen?: boolean

  popupClassName?: string

  isShared?: boolean

  allowedActions?: ProjectActionType[]
  additionalActions?: Action[]
}

const collisionAvoidance: Menu.Positioner.Props["collisionAvoidance"] = {
  align: "flip",
  side: "flip"
}

export const ProjectActionsButton = React.memo(
  ({
    isDisabled,
    isShared,
    isDefaultOpen = false,
    popupClassName,
    className,
    allowedActions,
    additionalActions,
    ...callbacks
  }: ProjectActionsButtonProps) => {
    const { t } = useTranslation()

    const { actions } = useProjectActions({
      idPrefix: "project__actions-menu",
      isShared,
      allowedActions,
      additionalActions,
      ...callbacks
    })

    if (actions.length === 0) {
      return null
    }

    return (
      <Menu.Root defaultOpen={isDefaultOpen}>
        <Menu.Trigger
          data-testid="project__actions"
          className={classNames(
            "btn",
            "btn-secondary",
            "btn--icon-only",
            className
          )}
          aria-label={t("Project.Actions.Actions")}
          disabled={isDisabled}
          onClick={preventDefaultAndStopPropagation}
        >
          <Icon name={IconName.KEBAB} size={IconSize.MEDIUM} />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner
            className="menu__positioner"
            align="end"
            side="bottom"
            sideOffset={16}
            positionMethod="fixed"
            collisionAvoidance={collisionAvoidance}
          >
            <Menu.Popup
              className={classNames("menu__popup", popupClassName)}
              data-testid="project-actions-popup"
              onClick={preventDefaultAndStopPropagation}
            >
              {actions.map(({ isDanger, onClick, ...action }) => {
                return (
                  <React.Fragment key={action.id}>
                    <Menu.Item
                      {...action}
                      onClick={onClick as React.MouseEventHandler<HTMLElement>}
                      className={classNames(
                        "btn",
                        "btn-menu",
                        "menu__item",
                        action.className,
                        { "btn--danger": isDanger }
                      )}
                      key={`${action.id}-action`}
                    >
                      {action.icon ? (
                        <Icon name={action.icon} key={`${action.id}-icon`} />
                      ) : null}
                      <span key={`${action.id}-label`}>{action.label}</span>
                    </Menu.Item>
                    <Menu.Separator
                      key={`${action.id}-separator`}
                      className="menu__separator"
                    />
                  </React.Fragment>
                )
              })}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    )
  }
)
